import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { BackendApiService } from 'src/app/core/services/backend-api';
import { Vendor } from '../models/database-models';
import {
  SystemConfigurationCode,
  SystemConfigurationService,
} from 'src/app/core/services/systemConfiguration.service';
import moment from 'moment';
import { ValidationErrorCategory } from '../models/app-models/ValidationErrorMessage.model';

@Injectable({ providedIn: 'root' })
export class ValidationService {
  constructor(
    private backEndService: BackendApiService,
    private systemConfigurationService: SystemConfigurationService
  ) {}

  public async validateGEWCA(
    errorMsgs: { message: string; category: ValidationErrorCategory }[],
    documentNo: string,
    vendors: Vendor[],
    validationErrorCategory: ValidationErrorCategory
  ) {
    // Check if there is validation bypass for GEWCA validation
    // If there is validation bypass, skip GEWCA validation
    let hasGEWCAValidationException = false;
    let isApiError = false;

    // Only get validation exceptions if there is MPDocumentNumber
    // If no MPDocumentNumber, then perform validation
    if (documentNo) {
      try {
        const validationExceptionsValid = await lastValueFrom(
          this.backEndService.getValidationExceptionsValid(documentNo!)
        );
        hasGEWCAValidationException =
          validationExceptionsValid?.DocumentRequiredGEWCA ?? false;
      } catch (err: any) {
        isApiError = true;
        errorMsgs.push({
          message:
            err ??
            'Failed to retrieve GEWCA validation exception for this document. Please try again later.',
          category: validationErrorCategory,
        });
      }
    }

    // If there is no validation bypass, perform GEWCA validation
    if (!isApiError && !hasGEWCAValidationException) {
      const activeVendors = vendors.filter((vendor) => !vendor.isDeleted && !vendor.Strikethrough);

      // If any vendor is non GEWCA, throw error
      const hasNonGEWCAVendor = activeVendors.some((vendor) => !Array.isArray(vendor.VendorGEWCAs) || vendor.VendorGEWCAs.length === 0);
      if (hasNonGEWCAVendor) {
        errorMsgs.push({
          message: `As you have GEWCA items, you are only allowed to submit GEWCA vendors.`,
          category: validationErrorCategory,
        });
      }

      // If any vendor has expiring soon GEWCA license, throw error
      const gewcaSystemConfiguration =
        this.systemConfigurationService.getSystemConfiguration(
          SystemConfigurationCode.GEWCALicenseExpiry
        );
      if (
        !gewcaSystemConfiguration ||
        !gewcaSystemConfiguration.Value ||
        !Number(gewcaSystemConfiguration.Value)
      ) {
        // Unable to retrieve system configuration to properly validate license expiry, throw error and prevent submit
        errorMsgs.push({
          message: `Unable to retrieve GEWCA license expiry configuration to perform validation. Please try again later.`,
          category: validationErrorCategory,
        });
      } else {
        // Got system configuration to validate license expiry, continue with license expiry validation
        const licenseExpiryDays = Number(gewcaSystemConfiguration.Value);
        const expiringGEWCAVendorNames = activeVendors.reduce(
          (acc: string[], cur) => {
            const { Name, VendorGEWCAs = [] } = cur;

            if (VendorGEWCAs.length > 0) {
              // Find earliest GEWCALicenseExpiryDate for this vendor
              let earliestGEWCALicenseExpiryDate = VendorGEWCAs.reduce(
                (acc, record) => {
                  const { GEWCALicenseExpiryDate } = record;
                  let newAcc = acc;

                  if (
                    GEWCALicenseExpiryDate &&
                    moment(GEWCALicenseExpiryDate).isValid() &&
                    (!acc ||
                      moment(GEWCALicenseExpiryDate).isBefore(moment(acc)))
                  ) {
                    newAcc = GEWCALicenseExpiryDate;
                  }
                  return newAcc;
                },
                ''
              );

              // No valid GEWCALicenseExpiryDate or earliest GEWCALicenseExpiryDate will expire within configured licenseExpiryDays
              if (
                !earliestGEWCALicenseExpiryDate ||
                moment()
                  .add(licenseExpiryDays, 'days')
                  .isAfter(moment(earliestGEWCALicenseExpiryDate))
              ) {
                acc.push(Name?.trim() ?? '');
              }
            }
            return acc;
          },
          []
        );

        if (expiringGEWCAVendorNames.length > 0) {
          errorMsgs.push({
            message: `Please modify the affected vendors with GEWCA licence expiring soon: ${expiringGEWCAVendorNames.join(
              ', '
            )}.`,
            category: validationErrorCategory,
          });
        }
      }
    }
  }
}
